// Re:Focus — Delegate Client Module
// Cap activity panel, delegate queue, artifact approval/discard/feedback,
// detail popup, snooze helpers, delegate history

import { e, confidenceGlyph, formatRelativeTime } from './utils.js';
import { openAiChat, closeAiChat } from './chat-client.js';

let _getAuthHeader = () => ({});
let _getBoardData = () => null;
let _loadOverrides = () => ({});
let _saveOverrides = (ov) => {};
let _renderFromState = () => {};
let _cloudSave = async (ov) => {};
let _cloudSync = () => {};
let _AI_NAME = 'Cap';
let _expandedCardsRef = { value: new Set() };
let _openWorkspace = () => {};
let _triggerManualRefresh = async (btn) => {};

// Module state
let delegateQueueCache = null;
let _prevDelegateQueueCache = null;
let _lastDelegateFullScan = 0;
let _lastActivityRef = { value: Date.now() };
let _discardUndoTimer = null;
let _discardUndoPending = null;

export function initDelegateClient({ getAuthHeader, getBoardData, loadOverrides, saveOverrides, renderFromState, cloudSave, cloudSync, getAiName, expandedCardsRef, openWorkspace, triggerManualRefresh, lastActivityRef }) {
  _getAuthHeader = getAuthHeader;
  _getBoardData = getBoardData;
  _loadOverrides = loadOverrides;
  _saveOverrides = saveOverrides;
  _renderFromState = renderFromState;
  _cloudSave = cloudSave;
  _cloudSync = cloudSync;
  if (getAiName) _AI_NAME = getAiName();
  if (expandedCardsRef) _expandedCardsRef = expandedCardsRef;
  if (openWorkspace) _openWorkspace = openWorkspace;
  if (triggerManualRefresh) _triggerManualRefresh = triggerManualRefresh;
  if (lastActivityRef) _lastActivityRef = lastActivityRef;
}

// ── Snooze helpers ──────────────────────────────────────────────────────────

export function isArtifactSnoozed(taskId) {
  const ov = _loadOverrides();
  const expiry = ov.snoozedArtifacts?.[taskId];
  if (!expiry) return false;
  if (Date.now() >= new Date(expiry).getTime()) {
    delete ov.snoozedArtifacts[taskId];
    _saveOverrides(ov);
    return false;
  }
  return true;
}

export function snoozeArtifact(taskId, durationLabel) {
  const ov = _loadOverrides();
  if (!ov.snoozedArtifacts) ov.snoozedArtifacts = {};
  let expiry;
  const now = new Date();
  if (durationLabel === '2h') {
    expiry = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  } else if (durationLabel === 'tonight') {
    expiry = new Date(now); expiry.setHours(21, 0, 0, 0);
    if (expiry <= now) expiry.setDate(expiry.getDate() + 1);
  } else if (durationLabel === 'tomorrow') {
    expiry = new Date(now); expiry.setDate(expiry.getDate() + 1); expiry.setHours(8, 0, 0, 0);
  }
  ov.snoozedArtifacts[taskId] = expiry.toISOString();
  _saveOverrides(ov);
  _renderFromState();
}

export function getSnoozedCount() {
  const ov = _loadOverrides();
  if (!ov.snoozedArtifacts) return 0;
  let count = 0;
  for (const [, expiry] of Object.entries(ov.snoozedArtifacts)) {
    if (Date.now() < new Date(expiry).getTime()) count++;
  }
  return count;
}

// ── Delegate history trail ──────────────────────────────────────────────────

export function writeDelegateHistory(taskId, cardId, type, action) {
  const ov = _loadOverrides();
  const boardData = _getBoardData();
  if (!ov.delegateHistory) ov.delegateHistory = {};
  if (!cardId && boardData) {
    const allCards2 = [];
    function collectCards3(cards) { cards.forEach(c => { allCards2.push(c); if (c.children) collectCards3(c.children); }); }
    (boardData.columns || []).forEach(col => collectCards3(col.projects || []));
    for (const c of allCards2) {
      if (c.tasks?.some(t => t.id === taskId)) { cardId = c.id; break; }
      if (ov.addedTasks?.[c.id]?.some(t => t.id === taskId)) { cardId = c.id; break; }
    }
  }
  if (!cardId) return;
  if (!ov.delegateHistory[cardId]) ov.delegateHistory[cardId] = [];
  let taskText = taskId;
  if (boardData) {
    const allCards = [];
    function collectCards2(cards) { cards.forEach(c => { allCards.push(c); if (c.children) collectCards2(c.children); }); }
    (boardData.columns || []).forEach(col => collectCards2(col.projects || []));
    const card = allCards.find(c => c.id === cardId);
    if (card?.tasks) {
      const task = card.tasks.find(t => t.id === taskId);
      if (task) taskText = task.text;
    }
    const added = ov.addedTasks?.[cardId];
    if (added) {
      const task = added.find(t => t.id === taskId);
      if (task) taskText = task.text;
    }
  }
  ov.delegateHistory[cardId].push({
    taskId, taskText, cardId, type, action,
    at: new Date().toISOString()
  });
  if (ov.delegateHistory[cardId].length > 10) {
    ov.delegateHistory[cardId] = ov.delegateHistory[cardId].slice(-10);
  }
  _saveOverrides(ov);
}

// ── Delegate queue — fetch from API, cache in memory ────────────────────────

export async function refreshDelegateQueue() {
  try {
    const now = Date.now();
    const recentActivity = (now - _lastActivityRef.value < 5 * 60 * 1000);
    const needsFullScan = recentActivity || (now - _lastDelegateFullScan > 5 * 60 * 1000);
    const url = needsFullScan ? '/api/v1/delegate' : '/api/v1/delegate?scanBoard=false';
    if (needsFullScan) _lastDelegateFullScan = now;

    const resp = await fetch(url, { headers: _getAuthHeader() });
    if (!resp.ok) return;
    const data = await resp.json();
    _prevDelegateQueueCache = delegateQueueCache;
    delegateQueueCache = data.queue || data.items || data || [];
    if (Array.isArray(delegateQueueCache)) checkDelegateTransitions();
  } catch (_e) { /* silently skip */ }
}

export function getDelegateQueueCache() { return delegateQueueCache; }

function checkDelegateTransitions() {
  if (!_prevDelegateQueueCache || !Array.isArray(_prevDelegateQueueCache)) return;
  const prevMap = {};
  _prevDelegateQueueCache.forEach(i => { prevMap[i.taskId || i.id] = i.status; });
  (delegateQueueCache || []).forEach(i => {
    const key = i.taskId || i.id;
    const prevStatus = prevMap[key];
    if (i.status === 'awaiting_approval' && prevStatus && prevStatus !== 'awaiting_approval') {
      const ov = _loadOverrides();
      const wasRevision = ov.delegateArtifacts?.[key]?.revising === true;
      showCapResultToast(wasRevision);
    }
  });
}

function showCapResultToast(isRevision = false) {
  let toast = document.getElementById('cap-result-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'cap-result-toast';
    toast.className = 'cap-result-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = isRevision
    ? `\u21bb ${_AI_NAME} has revised this — ready to review`
    : `\u25c8 ${_AI_NAME} has a result ready for review`;
  toast.classList.add('visible');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('visible'), 4000);
}

// ── Card-level delegate — prompt modal + context builder ────────────────────

export function buildCardDelegateContext(proj) {
  const ov = _loadOverrides();
  const tasks = (proj.tasks || []).map(t => ({ text: t.text, done: !!t.done }));
  const contacts = (proj.contacts || []).map(c => ({ name: c.name, role: c.email || '' }));
  const cardArtKey = 'card:' + proj.id;
  const artifacts = ov.delegateArtifacts || {};
  const recentArtifacts = [];
  if (artifacts[cardArtKey]) {
    const a = artifacts[cardArtKey];
    recentArtifacts.push({ instruction: a.taskText || '', outcome: (a.content || '').slice(0, 200) });
  }
  for (const t of (proj.tasks || []).slice(-5).reverse()) {
    if (artifacts[t.id] && recentArtifacts.length < 2) {
      recentArtifacts.push({ instruction: t.text || '', outcome: (artifacts[t.id].content || '').slice(0, 200) });
    }
  }
  return {
    cardName: proj.name || '',
    status: proj.status || 'Active',
    scope: proj.outcome || proj.scope || '',
    tasks,
    contacts,
    recentArtifacts,
  };
}

export function enqueueCardDelegate(proj, instruction) {
  const ctx = buildCardDelegateContext(proj);
  const contextLines = [];
  if (ctx.tasks.length) contextLines.push('Tasks: ' + ctx.tasks.map(t => (t.done ? '[x] ' : '[ ] ') + t.text).join('; '));
  if (ctx.contacts.length) contextLines.push('Contacts: ' + ctx.contacts.map(c => c.name + (c.role ? ' (' + c.role + ')' : '')).join(', '));
  if (ctx.recentArtifacts.length) contextLines.push('Recent artifacts: ' + ctx.recentArtifacts.map(a => a.instruction + ' → ' + a.outcome.slice(0, 80)).join(' | '));
  fetch('/api/v1/delegate', {
    method: 'POST',
    headers: { ..._getAuthHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cardId: proj.id,
      cardName: proj.name || '',
      taskId: null,
      taskText: instruction.slice(0, 500),
      cardScope: ctx.scope || '',
      cardNotes: contextLines.join('\n'),
    }),
  }).catch(err => console.warn('[delegate] card-level enqueue failed:', err.message));
}

export function showDelegateToast(msg) {
  let toast = document.getElementById('delegate-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'delegate-toast';
    toast.className = 'delegate-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.classList.add('visible');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('visible'), 3000);
}

export function openDelegatePrompt(proj) {
  const existing = document.getElementById('delegate-prompt-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'delegate-prompt-overlay';
  overlay.className = 'delegate-prompt-overlay';
  overlay.innerHTML = `
    <div class="delegate-prompt-modal">
      <div class="delegate-prompt-header">
        <svg class="delegate-icon" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 8h8m0 0L8 5m3 3L8 11"/><path d="M12 3h1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-1"/></svg>
        <span>Delegate to ${e(_AI_NAME)}</span>
        <span class="delegate-prompt-card-name">${e(proj.name)}</span>
      </div>
      <textarea class="delegate-prompt-input" placeholder="What should ${e(_AI_NAME)} do with this card? e.g. draft a follow-up email, research options..." autofocus></textarea>
      <div class="delegate-prompt-actions">
        <button class="delegate-prompt-cancel">Cancel</button>
        <button class="delegate-prompt-submit">Delegate</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const input = overlay.querySelector('.delegate-prompt-input');
  const submitBtn = overlay.querySelector('.delegate-prompt-submit');
  const cancelBtn = overlay.querySelector('.delegate-prompt-cancel');

  function close() { overlay.remove(); }
  function submit() {
    const text = input.value.trim();
    if (!text) return;
    enqueueCardDelegate(proj, text);
    close();
    showDelegateToast(`Delegated to ${_AI_NAME} \u2197`);
  }

  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  cancelBtn.addEventListener('click', close);
  submitBtn.addEventListener('click', submit);
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); submit(); }
    if (ev.key === 'Escape') close();
  });
  requestAnimationFrame(() => input.focus());
}

// ── Discard undo toast ──────────────────────────────────────────────────────

export function showDiscardUndoToast(taskId, delegateId, artifactSnapshot) {
  if (_discardUndoTimer) { clearTimeout(_discardUndoTimer); _discardUndoTimer = null; }
  _discardUndoPending = { taskId, delegateId, artifactSnapshot };

  let toast = document.getElementById('discard-undo-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'discard-undo-toast';
    toast.className = 'discard-undo-toast';
    document.body.appendChild(toast);
  }
  toast.innerHTML = `<span class="discard-undo-label">Artifact discarded \u2014 Undo?</span><button class="discard-undo-btn">Undo</button>`;
  toast.classList.add('visible');

  toast.querySelector('.discard-undo-btn').addEventListener('click', () => {
    clearTimeout(_discardUndoTimer);
    _discardUndoTimer = null;
    if (_discardUndoPending) {
      const ov2 = _loadOverrides();
      if (!ov2.delegateArtifacts) ov2.delegateArtifacts = {};
      ov2.delegateArtifacts[_discardUndoPending.taskId] = _discardUndoPending.artifactSnapshot;
      _saveOverrides(ov2);
      _discardUndoPending = null;
      _renderFromState();
    }
    toast.classList.remove('visible');
  });

  _discardUndoTimer = setTimeout(() => {
    toast.classList.remove('visible');
    if (_discardUndoPending && _discardUndoPending.artifactSnapshot) {
      const snap = _discardUndoPending.artifactSnapshot;
      const isCardLevel = _discardUndoPending.taskId.startsWith('card:');
      const cardId = isCardLevel ? _discardUndoPending.taskId.slice(5) : (snap.cardId || '');
      writeDelegateHistory(_discardUndoPending.taskId, cardId, snap.type || 'result', 'discarded');
    }
    if (_discardUndoPending && _discardUndoPending.delegateId) {
      fetch(`/api/v1/delegate/reject/${_discardUndoPending.delegateId}`, {
        method: 'POST',
        headers: _getAuthHeader(),
      }).catch(err => console.warn('[delegate] reject failed:', err.message));
    }
    _discardUndoPending = null;
  }, 3000);
}

// ── Email send confirmation modal ───────────────────────────────────────────

export function showSendConfirmModal(artifact, onConfirm) {
  let overlay = document.getElementById('send-confirm-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'send-confirm-overlay';
    overlay.className = 'send-confirm-overlay';
    document.body.appendChild(overlay);
  }
  const to = artifact.to || artifact.recipient || '(unknown)';
  const subject = artifact.subject || '(no subject)';
  const preview = (artifact.content || '').slice(0, 150) + ((artifact.content || '').length > 150 ? '\u2026' : '');
  overlay.innerHTML = `<div class="send-confirm-box">
    <h3>Confirm Send</h3>
    <div class="send-confirm-field"><strong>To:</strong> ${e(to)}</div>
    <div class="send-confirm-field"><strong>Subject:</strong> ${e(subject)}</div>
    <div class="send-confirm-preview">${e(preview)}</div>
    <div class="send-confirm-actions">
      <button class="send-confirm-cancel">Go Back</button>
      <button class="send-confirm-send">Send Email</button>
    </div>
  </div>`;
  overlay.classList.remove('hidden');

  const close = () => { overlay.classList.add('hidden'); };
  overlay.querySelector('.send-confirm-cancel').addEventListener('click', close);
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  overlay.querySelector('.send-confirm-send').addEventListener('click', () => {
    close();
    onConfirm();
  });
  const escHandler = (ev) => { if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', escHandler); } };
  document.addEventListener('keydown', escHandler);
}

// ── Cap Activity Detail Popup ───────────────────────────────────────────────

let _closeCapActivityFn = () => {};
let _renderCapActivityPanelFn = async () => {};

export function openCapDetailPopup(key, artifact, clickedRow, { focusFeedback = false } = {}) {
  closeCapDetailPopup();

  const panelEl = document.getElementById('cap-activity-panel');
  const panelRect = panelEl.getBoundingClientRect();
  const rowRect = clickedRow.getBoundingClientRect();
  const popupW = Math.min(680, window.innerWidth * 0.6);
  const popupH = Math.min(500, window.innerHeight * 0.7);

  let popupLeft = panelRect.right + 8;
  if (popupLeft + popupW > window.innerWidth - 16) {
    popupLeft = panelRect.left - popupW - 8;
  }
  let popupTop = Math.max(16, Math.min(rowRect.top, window.innerHeight - popupH - 16));

  const scrim = document.createElement('div');
  scrim.id = 'cap-detail-scrim';
  scrim.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.15);z-index:1099`;
  document.body.appendChild(scrim);

  const popup = document.createElement('div');
  popup.id = 'cap-detail-popup';
  popup.style.cssText = `position:fixed;left:${popupLeft}px;top:${popupTop}px;width:${popupW}px;height:${popupH}px;z-index:1200;background:var(--surface-raised);border:1px solid var(--border);border-radius:10px;box-shadow:0 12px 48px rgba(0,0,0,0.5);display:flex;flex-direction:column;overflow:hidden`;

  const typeLabel = artifact.type === 'email_draft' ? '\ud83d\udce7 Email draft' : artifact.type === 'research' ? '\ud83d\udd0d Research' : artifact.type === 'document' ? '\ud83d\udcc4 Document' : '\u25c8 Result';
  const isEmailDraft = artifact.type === 'email_draft';
  const confHtml = artifact.confidence ? `<span class="artifact-confidence artifact-confidence-${artifact.confidence}">${confidenceGlyph(artifact.confidence)} ${e(artifact.confidence)}</span>` : '';
  const fullContent = artifact.content || '';
  const reqText = artifact.taskText || key;

  popup.innerHTML = `
    <div class="cdp-header">
      <span class="cdp-type">${typeLabel}</span> ${confHtml}
      <span class="cdp-card-name">${e(artifact.cardName || '')}</span>
      <button class="cdp-close" title="Close">\u2715</button>
    </div>
    ${focusFeedback ? `<div class="cdp-feedback-mode-banner">\u21a9 Requesting changes for: <strong>${e(artifact.cardName || key)}</strong></div>` : ''}
    <div class="cdp-body">
      <div class="ws-artifact-section-label">\u2500 What was requested</div>
      <div class="ws-artifact-request">${e(reqText)}</div>
      <div class="ws-artifact-section-label">\u2500 ${e(_AI_NAME)}'s response</div>
      <div class="cdp-content-scroll">
        ${isEmailDraft ? (
          (artifact.to || artifact.recipient)
            ? `<div class="ws-artifact-meta"><strong>To:</strong> ${e(artifact.to || artifact.recipient)}</div>`
            : `<div class="ws-artifact-meta ws-artifact-meta-warn">\u26a0\ufe0f No recipient \u2014 add one before sending</div>
               <div class="ws-artifact-meta"><strong>To:</strong> <input class="cdp-to-input" type="email" placeholder="recipient@example.com" value="" style="border:1px solid var(--border);border-radius:4px;padding:2px 6px;font:inherit;width:220px;background:var(--surface);color:var(--text)"/></div>`
        ) : (artifact.to ? `<div class="ws-artifact-meta"><strong>To:</strong> ${e(artifact.to)}</div>` : '')}
        ${artifact.subject ? `<div class="ws-artifact-meta"><strong>Subject:</strong> ${e(artifact.subject)}</div>` : ''}
        <pre class="ws-artifact-content">${e(fullContent)}</pre>
      </div>
      <div class="cdp-feedback-section" id="cdp-feedback-section">
        <div class="ws-artifact-section-label">\u2500 Request changes</div>
        <div class="ws-feedback-area">
          <textarea class="cdp-feedback-input" placeholder="Tell ${e(_AI_NAME)} what to change\u2026" rows="3"></textarea>
          <button class="cdp-feedback-send">Send to ${e(_AI_NAME)}</button>
        </div>
      </div>
    </div>
    <div class="cdp-footer">
      <button class="cdp-open-workspace" data-key="${e(key)}">\u25f3 Open in Card Workspace</button>
      <button class="cdp-approve" data-key="${e(key)}" data-delegate-id="${e(artifact.delegateId || '')}" data-artifact-type="${e(artifact.type || '')}">${isEmailDraft ? '\u2713 Approve & Send' : '\u2713 Approve'}</button>
      <button class="cdp-request-changes">\u21a9 Request changes</button>
      <button class="cdp-discard" data-key="${e(key)}" data-delegate-id="${e(artifact.delegateId || '')}">✗ Discard</button>
    </div>
  `;
  document.body.appendChild(popup);

  // Wire inline TO input
  const toInput = popup.querySelector('.cdp-to-input');
  if (toInput) {
    toInput.addEventListener('change', () => {
      const val = toInput.value.trim();
      if (val) {
        const ov2 = _loadOverrides();
        if (ov2.delegateArtifacts?.[key]) {
          ov2.delegateArtifacts[key].to = val;
          _saveOverrides(ov2);
        }
      }
    });
  }

  const close = () => closeCapDetailPopup();
  popup.querySelector('.cdp-close').addEventListener('click', close);
  scrim.addEventListener('click', close);
  const escHandler = (ev) => { if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', escHandler); } };
  document.addEventListener('keydown', escHandler);

  // Wire approve
  popup.querySelector('.cdp-approve').addEventListener('click', () => {
    const isCardLevel = key.startsWith('card:');
    const delegateId = artifact.delegateId || '';
    performCapActivityApprove(key, isCardLevel, delegateId);
    close();
    _renderCapActivityPanelFn();
  });

  // Wire discard
  popup.querySelector('.cdp-discard').addEventListener('click', () => {
    const delegateId = artifact.delegateId || '';
    const ov2 = _loadOverrides();
    const snap = ov2.delegateArtifacts?.[key] ? JSON.parse(JSON.stringify(ov2.delegateArtifacts[key])) : null;
    const ov3 = _loadOverrides();
    if (ov3.delegateArtifacts) delete ov3.delegateArtifacts[key];
    _saveOverrides(ov3);
    _renderFromState();
    showDiscardUndoToast(key, delegateId, snap);
    close();
    _renderCapActivityPanelFn();
  });

  // Wire open workspace
  popup.querySelector('.cdp-open-workspace').addEventListener('click', () => {
    const isCardLevel = key.startsWith('card:');
    const cardId = isCardLevel ? key.slice(5) : (artifact.cardId || '');
    close();
    _closeCapActivityFn();
    if (cardId) {
      _expandedCardsRef.value.add(cardId);
      _renderFromState();
      setTimeout(() => _openWorkspace(cardId, { taskId: key }), 100);
    }
  });

  // Wire request changes button
  popup.querySelector('.cdp-request-changes').addEventListener('click', () => {
    const textarea = popup.querySelector('.cdp-feedback-input');
    if (textarea) {
      textarea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      textarea.focus();
    }
  });

  // Wire feedback send
  popup.querySelector('.cdp-feedback-send').addEventListener('click', async () => {
    const feedbackText = popup.querySelector('.cdp-feedback-input').value.trim();
    if (!feedbackText) return;
    const sendBtn = popup.querySelector('.cdp-feedback-send');
    const ov2 = _loadOverrides();
    const art = ov2.delegateArtifacts?.[key];
    if (!art) return;
    const isCardLevel = key.startsWith('card:');
    const cardId = isCardLevel ? key.slice(5) : (art.cardId || '');
    const originalTask = art.taskText || key;
    const feedbackNote = `[Revision request: ${feedbackText}]\nOriginal result:\n${(art.content || '').slice(0, 500)}`;

    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending\u2026';

    if (!ov2.delegateArtifacts) ov2.delegateArtifacts = {};
    ov2.delegateArtifacts[key] = { ...art, revising: true, revisingNote: feedbackText };
    _saveOverrides(ov2);

    try {
      await fetch('/api/v1/delegate', {
        method: 'POST',
        headers: { ..._getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId, cardName: art.cardName || '', taskId: key, taskText: `${originalTask} @delegate`, cardScope: '', cardNotes: feedbackNote }),
      });
      sendBtn.textContent = '\u2713 Sent \u2014 Cap is revising';
      setTimeout(() => {
        close();
        _renderCapActivityPanelFn();
        _renderFromState();
      }, 800);
    } catch (err) {
      console.warn('[delegate] feedback re-queue failed:', err);
      sendBtn.disabled = false;
      sendBtn.textContent = 'Failed \u2014 try again';
    }
  });
}

export function closeCapDetailPopup() {
  const popup = document.getElementById('cap-detail-popup');
  if (popup) popup.remove();
  const scrim = document.getElementById('cap-detail-scrim');
  if (scrim) scrim.remove();
}

// ── Cap Activity Approve ────────────────────────────────────────────────────

export function performCapActivityApprove(taskId, isCardLevel, delegateId) {
  const ov0 = _loadOverrides();
  const artifact = ov0.delegateArtifacts?.[taskId];
  if (artifact) writeDelegateHistory(taskId, isCardLevel ? taskId.slice(5) : (artifact.cardId || ''), artifact.type || 'result', 'approved');

  if (delegateId) {
    const hdrs = _getAuthHeader();
    hdrs['Content-Type'] = 'application/json';
    fetch(`/api/v1/delegate/approve/${delegateId}`, { method: 'POST', headers: hdrs, body: JSON.stringify({}) })
      .then(r => r.json()).then(res => {
        const ov2 = _loadOverrides();
        if (res.status !== 'approved_pending_send') {
          if (isCardLevel) {
            const cardId = taskId.slice(5);
            if (!ov2.cardStatusOverrides) ov2.cardStatusOverrides = {};
            ov2.cardStatusOverrides[cardId] = { status: 'Done', doneAt: new Date().toISOString() };
            if (!ov2.cardStatus) ov2.cardStatus = {};
            ov2.cardStatus[cardId] = 'Done';
          } else {
            if (!ov2.taskStates) ov2.taskStates = {};
            ov2.taskStates[taskId] = true;
            ov2.taskCompletedAt = ov2.taskCompletedAt || {};
            ov2.taskCompletedAt[taskId] = new Date().toISOString();
            if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
            ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
          }
        }
        if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
        _saveOverrides(ov2);
        _renderFromState();
        _renderCapActivityPanelFn();
      }).catch(err => console.warn('[delegate] cap-activity approve failed:', err.message));
  } else {
    const ov2 = _loadOverrides();
    if (isCardLevel) {
      const cardId = taskId.slice(5);
      if (!ov2.cardStatusOverrides) ov2.cardStatusOverrides = {};
      ov2.cardStatusOverrides[cardId] = { status: 'Done', doneAt: new Date().toISOString() };
      if (!ov2.cardStatus) ov2.cardStatus = {};
      ov2.cardStatus[cardId] = 'Done';
    } else {
      if (!ov2.taskStates) ov2.taskStates = {};
      ov2.taskStates[taskId] = true;
      ov2.taskCompletedAt = ov2.taskCompletedAt || {};
      ov2.taskCompletedAt[taskId] = new Date().toISOString();
      if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
      ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
    }
    if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
    _saveOverrides(ov2);
    _renderFromState();
    _renderCapActivityPanelFn();
  }
}

// ── Cap Activity Badge ──────────────────────────────────────────────────────

export function updateCapActivityBadge() {
  const capActBadge = document.getElementById('cap-activity-badge');
  if (!capActBadge) return;
  const queue = Array.isArray(delegateQueueCache) ? delegateQueueCache : [];
  const ov = _loadOverrides();
  const artifactCount = Object.keys(ov.delegateArtifacts || {}).length;
  const pendingCount = queue.filter(i => i.status === 'pending' || i.status === 'in_progress' || i.status === 'awaiting_approval').length;
  const total = pendingCount + artifactCount;
  if (total > 0) {
    capActBadge.textContent = total;
    capActBadge.classList.remove('hidden');
  } else {
    capActBadge.classList.add('hidden');
  }
}

// ── Cap Activity Panel Setup ────────────────────────────────────────────────

export function setupCapActivityPanel() {
  const capActBtn = document.getElementById('cap-activity-btn');
  const capActOverlay = document.getElementById('cap-activity-overlay');
  const capActClose = document.getElementById('cap-activity-close');
  const capActBody = document.getElementById('cap-activity-body');

  function openCapActivity() {
    capActOverlay.classList.remove('hidden');
    renderCapActivityPanel();
    openAiChat();
  }
  function closeCapActivity() {
    capActOverlay.classList.add('hidden');
    closeAiChat();
  }
  _closeCapActivityFn = closeCapActivity;

  capActBtn.addEventListener('click', openCapActivity);
  capActClose.addEventListener('click', closeCapActivity);
  capActOverlay.addEventListener('click', (ev) => {
    if (ev.target === capActOverlay) closeCapActivity();
  });

  // Refresh button
  const capRefreshBtn = document.getElementById('cap-activity-refresh-btn');
  if (capRefreshBtn && !capRefreshBtn._wired) {
    capRefreshBtn._wired = true;
    capRefreshBtn.addEventListener('click', async () => {
      await _triggerManualRefresh(capRefreshBtn);
      renderCapActivityPanel();
    });
  }

  // Clear all artifacts
  const capClearBtn = document.getElementById('cap-activity-clear-btn');
  if (capClearBtn) {
    capClearBtn.addEventListener('click', async () => {
      if (!confirm('Clear all pending artifacts and delegate history? This cannot be undone.')) return;
      capClearBtn.disabled = true;
      capClearBtn.textContent = 'Clearing…';
      const ov = _loadOverrides();
      delete ov.delegateArtifacts;
      delete ov.delegateHistory;
      delete ov.suggestPrompts;
      _saveOverrides(ov);
      await _cloudSave(ov);
      capClearBtn.textContent = 'Cleared ✓';
      setTimeout(() => { capClearBtn.textContent = 'Clear all'; capClearBtn.disabled = false; }, 1500);
      openCapActivity();
    });
  }
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && !capActOverlay.classList.contains('hidden')) {
      closeCapActivity();
    }
  });

  async function renderCapActivityPanel() {
    capActBody.innerHTML = '<div class="api-keys-loading">Loading\u2026</div>';
    try {
      const resp = await fetch('/api/v1/delegate', { headers: _getAuthHeader() });
      if (!resp.ok) { capActBody.innerHTML = `<div class="cap-activity-empty">Could not load ${e(_AI_NAME)} data.</div>`; return; }
      const data = await resp.json();
      const queue = data.queue || data.items || data || [];
      const items = Array.isArray(queue) ? queue : [];

      const inProgress = items.filter(i => i.status === 'pending' || i.status === 'in_progress');
      const awaiting = items.filter(i => i.status === 'awaiting_approval');

      const ov = _loadOverrides();
      const localArtifacts = ov.delegateArtifacts || {};
      const revisingEntries = Object.entries(localArtifacts).filter(([, art]) => art.revising === true);
      const artifactEntries = Object.entries(localArtifacts).filter(([, art]) => !art.revising);

      let html = '';

      // In Progress section
      html += '<div class="cap-activity-section"><h4>In Progress</h4>';
      if (inProgress.length || revisingEntries.length) {
        if (inProgress.length) {
          html += inProgress.map(i => `<div class="cap-activity-item"><span class="cap-activity-item-text">${e(i.taskText || i.text || i.taskId || 'Task')}</span><span class="cap-activity-item-card">${e(i.cardName || '')}</span></div>`).join('');
        }
        if (revisingEntries.length) {
          html += revisingEntries.map(([, art]) => `
            <div class="cap-activity-item cap-activity-revising-item">
              <span class="cap-revising-badge">\u21bb Revising</span>
              <span class="cap-activity-item-card">${e(art.cardName || '')}</span>
              ${art.revisingNote ? `<div class="cap-revising-note">\u201c${e(art.revisingNote)}\u201d</div>` : ''}
            </div>`).join('');
        }
      } else {
        html += '<div class="cap-activity-item-empty">No tasks in progress</div>';
      }
      html += '</div>';

      // Awaiting Review section
      html += '<div class="cap-activity-section"><h4>Awaiting Your Review</h4>';
      if (artifactEntries.length) {
        html += artifactEntries.map(([key, art]) => {
          const typeLabel = art.type === 'email_draft' ? '\ud83d\udce7 Email draft' : art.type === 'research' ? '\ud83d\udd0d Research' : art.type === 'document' ? '\ud83d\udcc4 Document' : '\u25c8 Result';
          const confHtml = art.confidence ? `<span class="cap-review-conf cap-review-conf-${art.confidence}">${confidenceGlyph(art.confidence)}</span>` : '';
          const isEmailDraft = art.type === 'email_draft';
          const asked = (art.taskText || '').slice(0, 60) + ((art.taskText || '').length > 60 ? '\u2026' : '');
          const preview = (art.content || '').slice(0, 80) + ((art.content || '').length > 80 ? '\u2026' : '');
          return `<div class="cap-activity-item cap-activity-review-item" data-artifact-key="${e(key)}">
            <div class="cap-review-row1"><span class="cap-review-type">${typeLabel}</span>${confHtml}<span class="cap-activity-item-card">${e(art.cardName || key)}</span></div>
            ${isEmailDraft ? `<div class="cap-review-asked"><span class="cap-review-label">To:</span> ${e(art.to || art.recipient || '\u2014')}</div>` : ''}
            <div class="cap-review-asked"><span class="cap-review-label">Asked:</span> ${e(asked)}</div>
            <div class="cap-review-preview"><span class="cap-review-label">Result:</span> ${e(preview)}</div>
            <div class="cap-activity-review-actions">
              <button class="cap-review-approve" data-artifact-key="${e(key)}" data-delegate-id="${e(art.delegateId || '')}" data-artifact-type="${e(art.type || '')}">${isEmailDraft ? '\u2713 Approve & Send' : '\u2713 Approve'}</button>
              <button class="cap-review-feedback" data-artifact-key="${e(key)}" title="Request changes">↩ Request changes</button>
              <button class="cap-review-discard" data-artifact-key="${e(key)}" data-delegate-id="${e(art.delegateId || '')}">✗ Discard</button>
            </div>
          </div>`;
        }).join('');
      } else if (awaiting.length) {
        html += awaiting.map(i => `<div class="cap-activity-item"><span class="cap-activity-item-text">${e(i.taskText || i.text || 'Result')}</span><span class="cap-activity-item-card">${e(i.cardName || '')}</span></div>`).join('');
      } else {
        html += '<div class="cap-activity-item-empty">Nothing awaiting review</div>';
      }
      html += '</div>';

      // Snoozed indicator
      const snoozedCount = getSnoozedCount();
      if (snoozedCount > 0) {
        html += `<div class="cap-activity-section"><div class="cap-snoozed-indicator">\u23f0 ${snoozedCount} snoozed</div></div>`;
      }

      // Last cron run
      try {
        const healthResp = await fetch('/api/v1/health', { headers: _getAuthHeader() });
        if (healthResp.ok) {
          const healthData = await healthResp.json();
          if (healthData.lastCron || healthData.lastRun) {
            html += `<div class="cap-activity-section"><h4>Last check-in</h4><div class="cap-activity-cron-row"><span class="cap-activity-item-empty">${formatRelativeTime(healthData.lastCron || healthData.lastRun)}</span><button class="cap-cron-refresh-btn health-refresh-btn">\u21bb Refresh now</button></div></div>`;
          }
        }
      } catch (_e) { /* skip */ }

      // Empty state
      if (!inProgress.length && !artifactEntries.length && !awaiting.length) {
        html = `<div class="cap-activity-empty">${e(_AI_NAME)} is caught up \u2014 nothing pending \u25c8</div>`;
      }

      capActBody.innerHTML = html;

      // Wire cron refresh
      const cronRefreshBtn = capActBody.querySelector('.cap-cron-refresh-btn');
      if (cronRefreshBtn) cronRefreshBtn.addEventListener('click', () => _triggerManualRefresh(cronRefreshBtn));

      // Wire row clicks
      capActBody.querySelectorAll('.cap-activity-review-item').forEach(row => {
        row.addEventListener('click', (ev) => {
          if (ev.target.closest('.cap-review-approve') || ev.target.closest('.cap-review-discard') || ev.target.closest('.cap-review-feedback')) return;
          const key = row.dataset.artifactKey;
          const ov2 = _loadOverrides();
          const art = ov2.delegateArtifacts?.[key];
          if (art) openCapDetailPopup(key, art, row);
        });
      });

      // Wire request-changes buttons
      capActBody.querySelectorAll('.cap-review-feedback').forEach(btn => {
        btn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const key = btn.dataset.artifactKey;
          const ov2 = _loadOverrides();
          const art = ov2.delegateArtifacts?.[key];
          const row = btn.closest('.cap-activity-review-item');
          if (art && row) {
            openCapDetailPopup(key, art, row, { focusFeedback: true });
            setTimeout(() => {
              const textarea = document.querySelector('.cdp-feedback-input');
              if (textarea) {
                textarea.focus();
                textarea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }, 60);
          }
        });
      });

      // Wire approve/discard buttons
      capActBody.querySelectorAll('.cap-review-approve').forEach(btn => {
        btn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const key = btn.dataset.artifactKey;
          const delegateId = btn.dataset.delegateId;
          const isCardLevel = key.startsWith('card:');
          const row = btn.closest('.cap-activity-review-item');
          performCapActivityApprove(key, isCardLevel, delegateId);
          if (row) {
            row.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
            row.style.overflow = 'hidden';
            row.style.opacity = '0';
            row.style.maxHeight = '0';
            setTimeout(() => renderCapActivityPanel(), 320);
          } else {
            renderCapActivityPanel();
          }
        });
      });
      capActBody.querySelectorAll('.cap-review-discard').forEach(btn => {
        btn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const key = btn.dataset.artifactKey;
          const delegateId = btn.dataset.delegateId;
          const ov2 = _loadOverrides();
          const artifact = ov2.delegateArtifacts?.[key];
          const snap = artifact ? JSON.parse(JSON.stringify(artifact)) : null;
          const ov3 = _loadOverrides();
          if (ov3.delegateArtifacts) delete ov3.delegateArtifacts[key];
          _saveOverrides(ov3);
          _renderFromState();
          showDiscardUndoToast(key, delegateId, snap);
          renderCapActivityPanel();
        });
      });
    } catch (_e) {
      capActBody.innerHTML = `<div class="cap-activity-empty">Could not reach ${e(_AI_NAME)}.</div>`;
    }
  }

  _renderCapActivityPanelFn = renderCapActivityPanel;

  // Badge update after delegate queue refresh + initial
  setTimeout(updateCapActivityBadge, 500);

  return { openCapActivity, closeCapActivity, renderCapActivityPanel };
}
